import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import SiteDialog from "../../components/SiteDialog";
import AppPagination from "../../components/AppPagination";
import theme from "../../utils/theme";
import { useAppSelector } from "../../hooks";
import { BasicDataTypes } from "../../types/types";
import { useLocation } from "react-router-dom";
import Loading from "../../components/Loading";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocationRequest } from "../../hooks/locationRequest";
import CreateLocation from "../../components/location/CreateLocation";
import UpdateLocation from "../../components/location/UpdateLocation";
import AlertDialog from '../../components/AlertDialog';
import SearchInput from '../../components/filters/SearchInput';
import RefreshButton from '../../components/filters/RefreshButton';

const LocationsList: React.FC = () => {
  const { search } = useLocation();
  const { loading, data: locationsData } = useAppSelector(state => state.location);
  const [locations, setLocations] = useState<BasicDataTypes[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(0);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [updateOpen, setUpdateOpen] = useState<boolean>(false);
  const [location, setLocation] = useState<BasicDataTypes | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [slug, setSlug] = useState<string | null>(null);
  const [ searchText,setSearchText ] = useState<string>("")
  const locationRequest = useLocationRequest();

  const handleEdit = useCallback((location: BasicDataTypes) => {
    setLocation(location);
    setUpdateOpen(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      await locationRequest({ method: 'GET', data: null, slug: null, query: null });
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, [locationRequest]);

  const handleDelete = (toDeleteSlug: string) => {
    setSlug(toDeleteSlug);
    setShowAlert(true);
  };

  const handleCancel = () => {
    setShowAlert(false);
  };

  const handleConfirm = useCallback(async () => {
    if (slug) {
      try {
        await locationRequest({ method: 'DELETE', data: null, slug, query: null });
        fetchData();
        setShowAlert(false);
      } catch (error) {
        console.error('Failed to delete location:', error);
      }
    }
  }, [locationRequest, fetchData, slug]);

  useEffect(() => {
    if (locationsData) {
      const { meta } = locationsData;
      setLastPage(Number(meta?.lastPage) || 1);
      setCurrentPage(Number(meta?.currentPage));
      setPerPage(Number(meta?.perPage));
      setLocations(locationsData.data);
    }
  }, [locationsData]);

  useEffect(() => {
    fetchData();
  }, [search, fetchData]);

  return (
    <Grid container columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid item xs={12}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: '8px' }}>
          {loading && <Loading />}
          <AlertDialog
            toggle={showAlert}
            setToggle={setShowAlert}
            cancel={handleCancel}
            confirm={handleConfirm}
            title={"Are you sure?"}
            content={"You want to delete this location."}
            type="error"
          />
          {location && (
            <SiteDialog
              open={updateOpen}
              setOpen={setUpdateOpen}
              dialogChildren={<UpdateLocation locationData={location} setDialogOpen={setUpdateOpen} />}
            />
          )}
          <Typography variant="h5" sx={{ mb: 3 }}>Locations List</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>Add New</Button>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <SearchInput
                searchText={searchText}
                setSearchText={setSearchText}
                placeholderText='Search by title'
              />
              <Box sx={{ mr: 3 }}></Box>
              <RefreshButton/>
            </Box>
            <SiteDialog
              open={createOpen}
              setOpen={setCreateOpen}
              dialogChildren={<CreateLocation setDialogOpen={setCreateOpen} />}
            />
          </Box>
          <TableContainer sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: '10px' }}>
            <Table sx={{ minWidth: 300 }} aria-label="caption table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="left">Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length > 0 ? locations.map((location, index) => (
                  <TableRow key={location.id}>
                    <TableCell align="center">{((currentPage - 1) * perPage) + (index + 1)}</TableCell>
                    <TableCell align="left">{location.name}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Button onClick={() => handleEdit(location)} sx={{ mr: 2 }} variant="contained" size="small" color="warning">
                        <EditIcon />
                      </Button>
                      <Button onClick={() => handleDelete(location.slug)} variant="contained" size="small" color="error">
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No categories found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <AppPagination pageCount={Number(lastPage)} />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default LocationsList;
